import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  CircleDot,
  GitBranch,
  Network,
  Play,
  RefreshCw,
  Rocket,
  UsersRound,
  X,
} from "lucide-react";
import { api, formatApiError } from "../api";
import { AppHeader, BrandedLoading } from "../components/app";
import type { InitialMatchingLaunchReadiness } from "../types";
import { RequireAdmin } from "./AdminPages";

const EXCLUSION_LABELS: Record<keyof InitialMatchingLaunchReadiness["exclusionReasons"], string> = {
  inactive_account: "Inactive account",
  unverified_email: "Email not verified",
  missing_profile: "No profile",
  incomplete_profile: "Incomplete profile",
  missing_preferences: "No preferences",
  insufficient_photos: "Fewer than 3 photos",
  pending_approval: "Awaiting approval",
  suspended: "Suspended",
  invalid_subscription: "No valid subscription",
  skip_period: "Matching pause",
  active_match: "Already in an active match",
};

function formatDate(value: string | null) {
  if (!value) return "Not calculated yet";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleString();
}

function RuleStatus({ passes, unavailable = false, waitingLabel = "Waiting" }: { passes: boolean; unavailable?: boolean; waitingLabel?: string }) {
  if (unavailable) return <span className="launch-rule-status is-muted"><CircleDot size={14} aria-hidden="true" /> Not applicable</span>;
  return passes
    ? <span className="launch-rule-status is-ready"><Check size={14} aria-hidden="true" /> Ready</span>
    : <span className="launch-rule-status"><X size={14} aria-hidden="true" /> {waitingLabel}</span>;
}

function LaunchDecision({ data }: { data: InitialMatchingLaunchReadiness }) {
  const launchComplete = data.phase === "live";
  const earlyUnavailable = data.rules.foundingMilestoneReached || launchComplete;
  return (
    <section className="launch-panel launch-decision-panel">
      <div className="launch-panel-head">
        <div><p className="eyebrow">Decision paths</p><h2>{launchComplete ? "Initial gate complete" : "Can the first round begin?"}</h2></div>
        <GitBranch aria-hidden="true" />
      </div>
      <div className="launch-rule-flow" role="group" aria-label="Initial launch decision diagram">
        <div className="launch-rule-source">
          <span>Current projection</span>
          <strong>{data.projectedMatches}</strong>
          <small>distinct introductions</small>
        </div>
        <div className="launch-rule-connector" aria-hidden="true" />
        <article className={`launch-rule-card${!launchComplete && data.rules.early.passes ? " is-ready" : ""}`}>
          <div><span>Early path</span><RuleStatus passes={data.rules.early.passes} unavailable={earlyUnavailable} /></div>
          <strong>{data.projectedMatches} / {data.rules.early.projectedMatchesRequired}</strong>
          <p>Before the balanced founding milestone, 30 distinct introductions are required.</p>
        </article>
        <article className={`launch-rule-card${!launchComplete && data.rules.standard.passes ? " is-ready" : ""}`}>
          <div><span>Standard path</span><RuleStatus passes={data.rules.standard.passes} unavailable={launchComplete} waitingLabel={data.rules.foundingMilestoneReached ? "Waiting" : "Pool building"} /></div>
          <strong>{data.projectedMatches} / {data.rules.standard.projectedMatchesRequired}</strong>
          <p>Requires at least 50 match-ready men, 50 match-ready women, and 15 distinct introductions.</p>
        </article>
      </div>
      <p className="launch-note">
        {launchComplete && "These thresholds no longer gate scheduled rounds. "}
        The projection uses the production solver, strict preferences, prior introductions,
        location rules, and the 65-point automated threshold. Admin-queued pairs may bypass the
        score threshold, but not eligibility or hard mutual requirements. Counts represent
        non-overlapping pairs, not candidate edges.
      </p>
    </section>
  );
}

function ReadinessFunnel({ data }: { data: InitialMatchingLaunchReadiness }) {
  const stages = [
    ["Registered", data.funnel.registered],
    ["Email verified", data.funnel.emailVerified],
    ["Profile + preferences", data.funnel.profileComplete],
    ["Photo-ready", data.funnel.photoReady],
    ["Approved", data.funnel.approved],
    ["Match-ready", data.funnel.matchReady],
  ] as const;
  const denominator = Math.max(1, data.funnel.registered);
  return (
    <section className="launch-panel">
      <div className="launch-panel-head"><div><p className="eyebrow">Readiness funnel</p><h2>From signup to matching</h2></div><UsersRound aria-hidden="true" /></div>
      <div className="launch-funnel">
        {stages.map(([label, value], index) => (
          <div className="launch-funnel-stage" key={label}>
            <div><span>{label}</span><strong>{value}</strong></div>
            <div className="launch-funnel-track" aria-hidden="true"><i style={{ width: `${value / denominator * 100}%` }} /></div>
            {index > 0 && <small>{stages[index - 1][1] - value} stopped before this stage</small>}
          </div>
        ))}
      </div>
    </section>
  );
}

function Diagnostics({ data }: { data: InitialMatchingLaunchReadiness }) {
  const exclusions = (Object.entries(data.exclusionReasons) as Array<[
    keyof InitialMatchingLaunchReadiness["exclusionReasons"],
    number,
  ]>)
    .filter(([, value]) => value > 0)
    .sort((left, right) => right[1] - left[1]);
  const largestExclusion = Math.max(1, ...exclusions.map(([, count]) => count));
  const graphCards = [
    ["Viable candidate edges", data.candidateGraph.viableEdges],
    ["No viable candidate", data.candidateGraph.membersWithNoViableCandidate],
    ["Median viable choices", data.candidateGraph.medianViableChoices],
    ["Maximum viable choices", data.candidateGraph.maximumViableChoices],
    ["Median selected score", data.candidateGraph.medianSelectedScore?.toFixed(1) ?? "—"],
  ] as const;
  return (
    <div className="launch-diagnostics-grid">
      <section className="launch-panel">
        <div className="launch-panel-head"><div><p className="eyebrow">Candidate graph</p><h2>Choice and coverage</h2></div><Network aria-hidden="true" /></div>
        <div className="launch-diagnostic-cards">
          {graphCards.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}
        </div>
        <p className="launch-note">A member with zero viable candidates remains ready for future rounds; Yara does not lower compatibility quality to force a pair.</p>
      </section>
      <section className="launch-panel">
        <div className="launch-panel-head"><div><p className="eyebrow">Exclusions</p><h2>Why profiles are not ready</h2></div><AlertTriangle aria-hidden="true" /></div>
        {exclusions.length ? (
          <div className="launch-exclusion-bars">
            {exclusions.map(([key, value]) => (
              <div key={key}>
                <div><span>{EXCLUSION_LABELS[key]}</span><strong>{value}</strong></div>
                <div aria-hidden="true"><i style={{ width: `${value / largestExclusion * 100}%` }} /></div>
              </div>
            ))}
          </div>
        ) : <p className="launch-empty-note">No profiles are currently excluded.</p>}
        <p className="launch-note">A profile may appear in more than one reason, so these counts should not be added together.</p>
      </section>
    </div>
  );
}

export function InitialMatchingLaunchDashboard({
  data,
  refreshing,
  starting,
  onRefresh,
  onStart,
}: {
  data: InitialMatchingLaunchReadiness;
  refreshing: boolean;
  starting: boolean;
  onRefresh: () => void;
  onStart: () => void;
}) {
  const canStart = data.phase === "collecting" && data.rules.canLaunch && data.calculationErrors.length === 0;
  const cards = [
    ["Match-ready men", `${data.readyMen} / 50`],
    ["Match-ready women", `${data.readyWomen} / 50`],
    ["Balanced progress", `${data.balancedProgress} / ${data.foundingProfileTarget}`],
    ["Projected matches", data.projectedMatches],
  ] as const;
  return (
    <>
      <section className={`launch-status-card launch-status-card--${data.phase}${canStart ? " is-ready" : ""}`}>
        <div className="launch-status-icon"><Rocket aria-hidden="true" /></div>
        <div>
          <span className="launch-phase-pill">{data.phase === "live" ? "Live" : canStart ? "Ready to launch" : "Collecting"}</span>
          <h1>{data.phase === "live" ? "The first matching rounds are live" : canStart ? "The founding pool is ready" : "Building the founding pool"}</h1>
          <p>{data.phase === "live"
            ? `Launched via the ${data.launchTrigger ?? "initial"} path on ${formatDate(data.launchedAt)}.`
            : canStart
              ? "A launch rule passes. Starting now will create the projected first round and notify each Person A."
              : "Matching remains closed until one of the agreed launch paths passes."}</p>
        </div>
        <div className="launch-actions">
          <button className="button secondary" type="button" disabled={refreshing || starting} onClick={onRefresh}>
            <RefreshCw className={refreshing ? "spin" : ""} size={17} aria-hidden="true" /> Recalculate
          </button>
          {data.phase === "collecting" && (
            <button className="button primary" type="button" disabled={!canStart || refreshing || starting} onClick={onStart}>
              <Play size={17} aria-hidden="true" /> {starting ? "Starting…" : "Start first round"}
            </button>
          )}
        </div>
      </section>

      <div className="launch-kpis">{cards.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}</div>
      {data.calculationErrors.length > 0 && <div className="banner error" role="alert">The projection encountered {data.calculationErrors.length} scoring error{data.calculationErrors.length === 1 ? "" : "s"}. Launch is disabled until the calculation completes cleanly.</div>}
      <LaunchDecision data={data} />
      <ReadinessFunnel data={data} />
      <Diagnostics data={data} />
      <p className="launch-calculated-at">Last calculated {formatDate(data.lastCalculatedAt)}. Readiness is a point-in-time operational estimate.</p>
    </>
  );
}

export function InitialMatchingLaunchPage() {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<{ tone: "success" | "error"; message: string } | null>(null);
  const query = useQuery({
    queryKey: ["initial-matching-launch-readiness"],
    queryFn: api.getInitialMatchingLaunchReadiness,
  });
  const recalculate = useMutation({
    mutationFn: api.recalculateInitialMatchingLaunch,
    onMutate: () => setNotice(null),
    onSuccess: (data) => {
      queryClient.setQueryData(["initial-matching-launch-readiness"], data);
      setNotice(data.calculationErrors.length > 0
        ? {
            tone: "error",
            message: `The projection finished with ${data.calculationErrors.length} scoring error${data.calculationErrors.length === 1 ? "" : "s"}. Launch remains disabled.`,
          }
        : { tone: "success", message: "Readiness projection updated." });
    },
  });
  const start = useMutation({
    mutationFn: api.startInitialMatchingLaunch,
    onMutate: () => setNotice(null),
    onSuccess: (response) => {
      queryClient.setQueryData(["initial-matching-launch-readiness"], response.readiness);
      const errorCount = response.result.errors.length;
      if (errorCount > 0) {
        setNotice({
          tone: "error",
          message: response.started
            ? `The launch is live, but only ${response.result.matchesCreated} introductions were created and ${errorCount} error${errorCount === 1 ? "" : "s"} occurred. Review the Worker logs before the next round.`
            : `The first round was not started because the latest calculation reported ${errorCount} error${errorCount === 1 ? "" : "s"}.`,
        });
        return;
      }
      setNotice({
        tone: "success",
        message: response.started
          ? `The first round is live with ${response.result.matchesCreated} introductions created.`
          : response.alreadyLive
            ? "The first matching rounds are already live."
            : "The pool changed and no longer passes a launch rule. The first round was not started.",
      });
    },
  });
  const actionError = recalculate.error || start.error;
  const confirmStart = () => {
    if (!window.confirm("Start Yara's first matching round now? This will create introductions and notify each Person A.")) return;
    start.mutate();
  };

  return (
    <RequireAdmin>
      <AppHeader title="Initial matching launch" backTo="/settings" historyBack />
      <div className="settings-document-scroll launch-document-scroll">
        <main className="launch-page">
          {notice && <div className={`banner ${notice.tone}`} role={notice.tone === "error" ? "alert" : "status"}>{notice.message}</div>}
          {actionError && <div className="banner error" role="alert">{formatApiError(actionError)}</div>}
          {query.isLoading
            ? <BrandedLoading message="Calculating launch readiness" />
            : query.error
              ? <div className="banner error" role="alert">{formatApiError(query.error)}</div>
              : query.data
                ? <InitialMatchingLaunchDashboard data={query.data} refreshing={recalculate.isPending} starting={start.isPending} onRefresh={() => recalculate.mutate()} onStart={confirmStart} />
                : null}
        </main>
      </div>
    </RequireAdmin>
  );
}
